/**
 * AttributionSection — SOURCE ATTRIBUTIE (links) + PAGINA'S-tabs (rechts),
 * op desktop naast elkaar, beide permanent zichtbaar.
 *
 * Positie in het dashboard: onder de Revenue/Calls-grafieken (hiërarchie
 * afgestemd 14 aug 2026). Sorteren via klikbare kolomkoppen; bron-filter via
 * de zichtbare dropdown in de tabellen-kop of door op een bron-rij te klikken.
 * De attributie-gezondheid (match rate/confidence/kwaliteit) staat als
 * gedempte voetregel onder in de bronnen-kaart: het is metadata over deze
 * tabellen, niet over de funnel. Filter en sortering overleven de
 * 60s-auto-refresh omdat de component bij een refresh gewoon blijft staan.
 */

import React, { useState } from 'react';
import {
  Compass, Link, Sparkles, Bot, MapPin, ChevronDown, ChevronUp,
} from 'lucide-react';
import { formatCurrency, formatNumber } from '../../utils/dashboardUtils';
import { formatPageUrl } from '../../utils/trackingHelpers';
import InfoDot from './InfoDot';

// Match-methodes voor de kwaliteits-popover in de voetregel — gesorteerd van
// hoogste naar laagste confidence.
const MATCH_METHODS = [
  { key: 'timestamp_phone', label: 'Klik + nummer', confidence: 95, color: '#16a34a', description: 'Bezoeker klikte de tel:-link én het nummer klopt exact.' },
  { key: 'session_daily_unique', label: 'Enige bezoeker die dag', confidence: 85, color: '#22c55e', description: 'Maar één bezoeker op die site die dag — vrijwel zeker dezelfde persoon.' },
  { key: 'timestamp', label: 'Klik op site', confidence: 80, color: '#2563eb', description: 'Bezoeker klikte een bel-knop op de juiste site binnen 5 minuten.' },
  { key: 'timestamp_recurring', label: 'Terugkerende beller', confidence: 75, color: '#475569', description: 'Beller heeft het nummer opgeslagen en belt terug — geen websitebezoek verwacht.' },
  { key: 'session_only', label: 'Sessie binnen 30 min', confidence: 60, color: '#f97316', description: 'Bezoeker was op de site, maar klikte niet op het nummer.' },
  { key: 'session_pool', label: 'Sessie in regio', confidence: 40, color: '#fb923c', description: 'Meerdere sites delen één nummer (bijv. Florida). Sessie gevonden in de regio.' },
  { key: 'session_daily_multiple', label: 'Meerdere bezoekers', confidence: 30, color: '#ef4444', description: 'Meerdere bezoekers die dag; dichtstbijzijnde sessie gekozen.' },
  { key: 'unmatched', label: 'Niet gematcht', confidence: null, color: '#94a3b8', description: 'Geen websitebezoek gevonden.' },
];

const CONFIDENCE_INFO_TEXT = 'Gewogen gemiddelde zekerheid (match confidence) over alle gematchte calls: '
  + '95 = klik op het nummer, 30 = zwakste sessie-match. Hoe hoger, hoe harder de bron-attributie.';

// Onder deze bezoekersaantallen is een conversiepercentage ruis (zelfde les als
// seo_conversion_tier: dunne steekproeven geven >100% of 0% zonder betekenis).
export const MIN_SESSIONS_FOR_CONVERSION = 25;

// Bronnen zonder zinvol conversiepercentage: Direct is een mengbak, en calls
// zonder gevonden sessie hebben per definitie geen bezoekers-denominator.
const NO_CONVERSION_SOURCES = new Set(['Direct', 'Onbekend (niet gematcht)']);

// Bronnen waar het pagina-filter niets zinnigs oplevert (geen sessie = geen
// pagina; "Overig" is een verzamelbak).
const NO_FILTER_SOURCES = new Set(['Onbekend (niet gematcht)', 'Overig (kleine bronnen)']);

// Sessie-only bronnen kleiner dan dit rollen op in "Overig (kleine bronnen)",
// anders vult de long tail van referral-hosts de lijst met 1-sessie-rijen.
const MIN_ROW_SESSIONS = 10;

// Source styling — AI-bronnen (ChatGPT/Perplexity/Copilot/...) krijgen eigen kleur + icoon zodat ze opvallen.
const SOURCE_STYLES = {
  'ChatGPT':            { color: '#10a37f', icon: Sparkles },   // groen — OpenAI tint
  'Perplexity':         { color: '#1e40af', icon: Bot },        // blauw
  'Copilot':            { color: '#7c3aed', icon: Bot },
  'Gemini':             { color: '#1a73e8', icon: Bot },
  'Claude':             { color: '#d97757', icon: Bot },
  'Google Ads':         { color: '#D02E17', icon: null },
  'Microsoft Ads':      { color: '#0891b2', icon: null },
  'Meta Ads':           { color: '#0866ff', icon: null },
  'Google Bedrijfsprofiel': { color: '#0f9d58', icon: MapPin },
  'Eigen netwerk':      { color: '#64748b', icon: Link },
  'Organisch (Google)': { color: '#2563eb', icon: null },
  'Organisch (Bing)':   { color: '#0891b2', icon: null },
  'Bing':               { color: '#0891b2', icon: null },
  'Organisch (DuckDuckGo)': { color: '#f59e0b', icon: null },
  'DuckDuckGo':         { color: '#f59e0b', icon: null },
  'Google Maps':        { color: '#0f9d58', icon: MapPin },
  'Facebook':           { color: '#0866ff', icon: null },
  'Instagram':          { color: '#e1306c', icon: null },
  'LinkedIn':           { color: '#0a66c2', icon: null },
  'WhatsApp':           { color: '#25d366', icon: null },
  'YouTube':            { color: '#dc2626', icon: null },
  'X (Twitter)':        { color: '#475569', icon: null },
  'Marktplaats':        { color: '#f59e0b', icon: null },
  'Direct':             { color: '#475569', icon: null },
  'Onbekend (niet gematcht)': { color: '#94a3b8', icon: null },
  'Overig (kleine bronnen)': { color: '#94a3b8', icon: null },
  'Referral':           { color: '#7c3aed', icon: null },
};

const getSourceStyle = (source) => {
  if (SOURCE_STYLES[source]) return SOURCE_STYLES[source];
  // "Referral: domein.nl" en overige "Organisch (...)" varianten op prefix stylen
  if (source && source.startsWith('Referral')) return SOURCE_STYLES['Referral'];
  if (source && source.startsWith('Organisch')) return { color: '#2563eb', icon: null };
  return { color: 'var(--color-brand-600, #D02E17)', icon: null };
};

// nl-NL percentage met 1 decimaal (18.7 -> "18,7%").
const fmtPct1 = (v) => (v == null ? null
  : `${v.toLocaleString('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`);

// Conversie-cel: altijd de waarde tonen, maar gedempt (indicatief) onder de
// bezoekers-drempel; alleen een em-dash als er geen denominator is.
const ConversionCell = ({ row }) => {
  if (row.conversion == null || NO_CONVERSION_SOURCES.has(row.source)) {
    return <span className="attr-num attr-num--muted">—</span>;
  }
  const overTitle = row.conversion > 100
    ? ' Boven 100%: calls kunnen horen bij een bezoeker die vóór deze periode voor het eerst kwam.'
    : '';
  if ((row.sessions || 0) < MIN_SESSIONS_FOR_CONVERSION) {
    return (
      <span
        className="attr-num attr-num--muted"
        title={`Indicatief: gebaseerd op minder dan ${MIN_SESSIONS_FOR_CONVERSION} bezoekers.${overTitle}`}
      >
        {fmtPct1(row.conversion)}
      </span>
    );
  }
  return (
    <span className="attr-num attr-num--conv" title={overTitle || undefined}>
      {fmtPct1(row.conversion)}
    </span>
  );
};

const SOURCE_INFO_TEXT = 'Bezoekers = unieke bezoekers die in de periode voor het eerst kwamen, ingedeeld op hun '
  + 'eerste bron (first-touch). Calls = álle oproepen; kan een call niet aan een bezoeker gekoppeld worden, '
  + 'dan staat hij bij "Onbekend (niet gematcht)". Leads = de gekwalificeerde oproepen die in rekening '
  + 'worden gebracht. Omzet = leads maal de leadprijs van de klant (zelfde prijsregels als de funnel en facturatie). Conversie = leads gedeeld door bezoekers; '
  + `onder de ${MIN_SESSIONS_FOR_CONVERSION} bezoekers staat het percentage gedempt (indicatief). Een call kan horen bij een bezoeker `
  + 'van vóór de periode, dus kleine bronnen kunnen boven 100% uitkomen. Direct en Onbekend krijgen geen conversie. '
  + 'Klik op een kolomkop om te sorteren; klik op een bron om de pagina\'s eronder op die bron te filteren.';

// Sorteerbare kolomkop: klik = sorteer aflopend, nogmaals = oplopend.
const SortHeader = ({ label, sortKey, sort, onSort, className }) => {
  const active = sort.key === sortKey;
  return (
    <button
      type="button"
      className={`${className || 'attr-col-btn'}${active ? ' attr-col-btn--active' : ''}`}
      onClick={() => onSort(sortKey)}
      title={active
        ? `Gesorteerd ${sort.dir === 'desc' ? 'aflopend' : 'oplopend'} — klik om om te draaien`
        : `Sorteer op ${label.toLowerCase()}`}
    >
      {label}
      {active && (sort.dir === 'desc'
        ? <ChevronDown size={10} strokeWidth={3} />
        : <ChevronUp size={10} strokeWidth={3} />)}
    </button>
  );
};

const AttributionSection = ({ attribution, funnelData, matchBreakdown }) => {
  const [sourceFilter, setSourceFilter] = useState(null);
  const [activeTab, setActiveTab] = useState('landing');   // 'landing' | 'clickers'
  const [showAllRows, setShowAllRows] = useState(false);
  const [srcSort, setSrcSort] = useState({ key: 'leads', dir: 'desc' });
  const [lpSort, setLpSort] = useState({ key: 'leads', dir: 'desc' });

  const sources = attribution?.sources || [];
  const landingPages = attribution?.landingPages || [];
  const topPages = attribution?.topPages || [];
  const visitors = attribution?.visitors || null;
  // Bezoekers-per-bron beschikbaar? (RPC gemigreerd + bereikbaar)
  const hasSessions = visitors !== null && sources.some((s) => s.sessions != null);

  if (sources.length === 0) return null;

  // Long tail van sessie-only bronnen (1 sessie per referral-host) oprollen.
  const visibleSources = [];
  const restRow = { source: 'Overig (kleine bronnen)', sessions: 0, calls: 0, leads: 0, revenue: 0, conversion: null };
  sources.forEach((r) => {
    if (r.calls > 0 || (r.sessions || 0) >= MIN_ROW_SESSIONS) visibleSources.push(r);
    else { restRow.sessions += (r.sessions || 0); restRow.calls += r.calls; restRow.leads += (r.leads || 0); restRow.revenue += (r.revenue || 0); }
  });
  if (restRow.sessions > 0) visibleSources.push(restRow);

  const makeSortToggle = (setter) => (key) => setter((prev) => (
    prev.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }
  ));
  const toggleSrcSort = makeSortToggle(setSrcSort);
  const toggleLpSort = makeSortToggle(setLpSort);

  const convSortValue = (r) => (
    r.conversion == null || NO_CONVERSION_SOURCES.has(r.source)
      || (r.sessions || 0) < MIN_SESSIONS_FOR_CONVERSION
      ? -1 : r.conversion
  );
  const numFor = (row, key) => {
    if (key === 'conversion') return convSortValue(row);
    if (key === 'sessions') return row.sessions || 0;
    if (key === 'calls') return row.calls || 0;
    if (key === 'clickers') return row.clickers || 0;
    if (key === 'revenue') return row.revenue || 0;
    return row.leads || 0;
  };
  const applySort = (rows, sort, tiebreak = ['leads', 'calls', 'sessions']) => [...rows].sort((a, b) => {
    const d = numFor(b, sort.key) - numFor(a, sort.key);
    if (d !== 0) return sort.dir === 'desc' ? d : -d;
    for (const t of tiebreak) {
      const td = numFor(b, t) - numFor(a, t);
      if (td !== 0) return td;
    }
    return 0;
  });

  const sortedSources = applySort(visibleSources, srcSort);
  const totalSrcCalls = sources.reduce((s, r) => s + r.calls, 0) || 1;
  const totalSrcLeads = sources.reduce((s, r) => s + (r.leads || 0), 0);
  const totalSrcRevenue = sources.reduce((s, r) => s + (r.revenue || 0), 0);
  const totalSrcSessions = hasSessions ? (visitors.total || sources.reduce((s, r) => s + (r.sessions || 0), 0)) : null;

  // ── Bron-filter ──────────────────────────────────────────────────────────
  const filterActive = Boolean(sourceFilter);
  const filterableSources = applySort(
    visibleSources.filter((r) => !NO_FILTER_SOURCES.has(r.source)),
    { key: 'leads', dir: 'desc' }
  ).map((r) => r.source);
  const toggleSourceFilter = (source) => {
    if (NO_FILTER_SOURCES.has(source)) return;
    setSourceFilter(sourceFilter === source ? null : source);
    setShowAllRows(false);
  };

  // Attributie-gezondheid voor de voetregel van de bronnen-kaart.
  const { matchedCalls = 0, totalCalls = 0 } = funnelData || {};
  const matchRate = totalCalls > 0 ? Math.round((matchedCalls / totalCalls) * 100) : null;
  // Groen pas vanaf 90%: sinds de waterval-matching zit de rate op ~93-98%,
  // dus 50% als groen-drempel maskeerde echte degradatie (qa-verbeterplan).
  const matchColor = matchRate == null ? 'var(--color-neutral-400)'
    : matchRate >= 90 ? 'var(--color-success, #22c55e)'
      : matchRate >= 60 ? 'var(--color-warning, #f59e0b)'
        : 'var(--color-error, #ef4444)';
  const avgConfidence = matchBreakdown?.avgConfidence ?? null;

  // Kwaliteits-breakdown als popover-inhoud (spans met display-overrides: de
  // popover is een <span role="tooltip">, dus geen block-elementen nesten).
  const qualityPanel = matchBreakdown && (() => {
    // recurring_from_history (server-bucket) valt onder "Terugkerende beller";
    // zonder deze merge waren die calls onzichtbaar en telden de %'s niet op.
    const breakdown = {
      ...matchBreakdown,
      timestamp_recurring: (matchBreakdown.timestamp_recurring || 0) + (matchBreakdown.recurring_from_history || 0),
    };
    const totalMatch = MATCH_METHODS.reduce((s, m) => s + (breakdown[m.key] || 0), 0) || 1;
    return (
      <span style={{ display: 'block' }}>
        <span className="funnel-info-title" style={{ display: 'block' }}>Attributie kwaliteit · {formatNumber(totalMatch)} calls</span>
        <span className="funnel-info-intro" style={{ display: 'block' }}>
          Elke oproep koppelen we aan een websitebezoek. De methode bepaalt hoe zeker we zijn:
        </span>
        <span className="funnel-info-list" style={{ display: 'flex' }}>
          {MATCH_METHODS.map((m) => {
            const count = breakdown[m.key] || 0;
            const pct = (count / totalMatch) * 100;
            if (m.key === 'timestamp' && count === 0) return null;
            return (
              <span key={m.key} className="quality-pop-row">
                <span className="quality-pop-top">
                  <span className="match-method-dot" style={{ background: m.color }} />
                  <span className="funnel-info-item-label">{m.label}</span>
                  {m.confidence !== null && (
                    <span
                      className="match-conf-chip"
                      style={{ color: m.color, background: `color-mix(in srgb, ${m.color} 13%, transparent)` }}
                    >
                      {m.confidence}%
                    </span>
                  )}
                  <span className="quality-pop-count">{formatNumber(count)} ({pct.toFixed(0)}%)</span>
                </span>
                <span className="match-method-bar-track" style={{ display: 'block' }}>
                  <span className="match-method-bar-fill" style={{ display: 'block', width: `${pct}%`, background: m.color }} />
                </span>
                <span className="funnel-info-item-desc" style={{ display: 'block' }}>{m.description}</span>
              </span>
            );
          })}
        </span>
      </span>
    );
  })();

  // Landing pages: onder een filter komen de cijfers uit perSource[bron];
  // bezoekers per bron is alleen bekend voor de top-pagina's uit de RPC (anders "—").
  const landingRows = landingPages
    .map((p) => {
      if (!filterActive) return p;
      const cell = (p.perSource || {})[sourceFilter];
      if (!cell) return null;
      const rowSessions = cell.sessions ?? null;
      const conversion = (rowSessions > 0) ? Math.round(((cell.leads || 0) / rowSessions) * 1000) / 10 : null;
      return { url: p.url, sessions: rowSessions, calls: cell.calls || 0, leads: cell.leads || 0, revenue: cell.revenue || 0, conversion };
    })
    .filter(Boolean)
    .filter((p) => !filterActive || p.calls > 0 || p.leads > 0 || (p.sessions || 0) > 0);

  const clickerRows = topPages
    .map((p) => {
      if (!filterActive) return { url: p.url, clickers: p.clickers };
      const n = (p.perSource || {})[sourceFilter] || 0;
      return n > 0 ? { url: p.url, clickers: n } : null;
    })
    .filter(Boolean);

  const activeRows = activeTab === 'landing'
    ? applySort(landingRows, lpSort)
    : applySort(clickerRows, { key: 'clickers', dir: 'desc' }, []);
  const visibleRows = showAllRows ? activeRows.slice(0, 25) : activeRows.slice(0, 5);
  const hasMoreRows = activeRows.length > 5;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6 items-start mb-6 lg:mb-8">
      {/* SOURCE ATTRIBUTIE — links */}
      <div className="table-container">
        <div className="funnel-diag-head">
          <Compass size={14} />
          <span className="funnel-diag-title">SOURCE ATTRIBUTIE</span>
          <InfoDot text={SOURCE_INFO_TEXT} />
          <span className="funnel-diag-right">
            {hasSessions
              ? `${formatNumber(totalSrcSessions)} bezoekers · ${formatNumber(totalSrcCalls)} calls · ${formatNumber(totalSrcLeads)} leads · ${formatCurrency(totalSrcRevenue)}`
              : `${formatNumber(totalSrcCalls)} calls · ${formatNumber(totalSrcLeads)} leads · ${formatCurrency(totalSrcRevenue)}`}
          </span>
        </div>
        <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {hasSessions && (
            <div className="source-attr-grid attr-grid-header">
              <span className="attr-col-spacer" />
              <SortHeader label="BEZOEKERS" sortKey="sessions" sort={srcSort} onSort={toggleSrcSort} />
              <SortHeader label="CALLS" sortKey="calls" sort={srcSort} onSort={toggleSrcSort} />
              <SortHeader label="LEADS" sortKey="leads" sort={srcSort} onSort={toggleSrcSort} />
              <SortHeader label="CONV." sortKey="conversion" sort={srcSort} onSort={toggleSrcSort} />
              <SortHeader label="OMZET" sortKey="revenue" sort={srcSort} onSort={toggleSrcSort} />
            </div>
          )}
          {sortedSources.map((row) => {
            const style = getSourceStyle(row.source);
            const SrcIcon = style.icon;
            const clickable = !NO_FILTER_SOURCES.has(row.source);
            const selected = sourceFilter === row.source;
            // Bar toont volume-aandeel: bezoekers als die er zijn, anders calls.
            const sharePct = hasSessions
              ? ((row.sessions || 0) / (totalSrcSessions || 1)) * 100
              : (row.calls / totalSrcCalls) * 100;
            const rowClass = [
              'source-attr-row',
              clickable && 'source-attr-row--clickable',
              selected && 'source-attr-row--selected',
              filterActive && !selected && 'source-attr-row--dimmed',
            ].filter(Boolean).join(' ');
            return (
              <div
                key={row.source}
                className={rowClass}
                onClick={clickable ? () => toggleSourceFilter(row.source) : undefined}
                onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSourceFilter(row.source); } } : undefined}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                aria-pressed={clickable ? selected : undefined}
                title={clickable ? (selected ? 'Klik om het bron-filter op te heffen' : `Toon de pagina's van ${row.source}`) : undefined}
              >
                <div className={`source-attr-grid${hasSessions ? '' : ' source-attr-grid--calls-only'}`}>
                  <span className="source-attr-badge">
                    <span className="source-attr-dot" style={{ background: style.color }} />
                    {SrcIcon && <SrcIcon size={12} strokeWidth={2.5} style={{ color: style.color, flexShrink: 0 }} />}
                    <span className="source-attr-label">{row.source}</span>
                  </span>
                  {hasSessions && (
                    <span className="attr-num">{row.sessions == null ? '—' : formatNumber(row.sessions)}</span>
                  )}
                  <span className="attr-num">
                    {formatNumber(row.calls)}
                    {!hasSessions && (
                      <span className="match-method-pct"> ({((row.calls / totalSrcCalls) * 100).toFixed(0)}%)</span>
                    )}
                  </span>
                  <span className="attr-num attr-num--leads">{formatNumber(row.leads || 0)}</span>
                  {hasSessions && <ConversionCell row={row} />}
                  <span className="attr-num">{formatCurrency(row.revenue || 0)}</span>
                </div>
                <div className="source-attr-bar-track">
                  <div
                    className="source-attr-bar-fill"
                    style={{ width: `${Math.min(sharePct, 100)}%`, background: style.color }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        {/* Attributie-gezondheid: metadata over deze tabellen, als gedempte voetregel */}
        {matchRate != null && (
          <div className="attr-health-foot">
            <span>
              Match rate <strong style={{ color: matchColor }}>{matchRate}%</strong>
              <span className="funnel-health-line-detail"> ({formatNumber(matchedCalls)}/{formatNumber(totalCalls)} calls gekoppeld aan een bezoeker)</span>
            </span>
            {avgConfidence != null && (
              <span>· gem. confidence <strong>{avgConfidence}%</strong> <InfoDot text={CONFIDENCE_INFO_TEXT} /></span>
            )}
            {qualityPanel && (
              <span>· kwaliteit per methode <InfoDot wide text={qualityPanel} /></span>
            )}
          </div>
        )}
      </div>

      {/* PAGINA'S — rechts, volgt het bron-filter */}
      <div className="table-container">
          <div className="funnel-diag-head">
            <div className="attr-tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'landing'}
                className={`attr-tab${activeTab === 'landing' ? ' attr-tab--active' : ''}`}
                onClick={() => { setActiveTab('landing'); setShowAllRows(false); }}
              >
                Landing pages
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'clickers'}
                className={`attr-tab${activeTab === 'clickers' ? ' attr-tab--active' : ''}`}
                onClick={() => { setActiveTab('clickers'); setShowAllRows(false); }}
              >
                Pagina's (klikkers)
              </button>
            </div>
            <label className="attr-source-select-wrap">
              <span className="attr-source-select-label">Bron:</span>
              <select
                className="attr-source-select"
                value={sourceFilter || ''}
                onChange={(e) => { setSourceFilter(e.target.value || null); setShowAllRows(false); }}
              >
                <option value="">alle bronnen</option>
                {filterableSources.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="overflow-x-auto">
            {activeTab === 'landing' ? (
              <table className="table-premium">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>LANDING PAGE</th>
                    {hasSessions && (
                      <th style={{ textAlign: 'right' }}>
                        <SortHeader label="BEZOEKERS" sortKey="sessions" sort={lpSort} onSort={toggleLpSort} className="th-sort" />
                      </th>
                    )}
                    <th style={{ textAlign: 'right' }}>
                      <SortHeader label="CALLS" sortKey="calls" sort={lpSort} onSort={toggleLpSort} className="th-sort" />
                    </th>
                    <th style={{ textAlign: 'right' }}>
                      <SortHeader label="LEADS" sortKey="leads" sort={lpSort} onSort={toggleLpSort} className="th-sort" />
                    </th>
                    {hasSessions && (
                      <th style={{ textAlign: 'right' }}>
                        <SortHeader label="CONV." sortKey="conversion" sort={lpSort} onSort={toggleLpSort} className="th-sort" />
                      </th>
                    )}
                    <th style={{ textAlign: 'right' }}>
                      <SortHeader label="OMZET" sortKey="revenue" sort={lpSort} onSort={toggleLpSort} className="th-sort" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((page, i) => (
                    <tr key={page.url}>
                      <td><span className="rank-badge">{i + 1}</span></td>
                      <td>
                        <a
                          href={page.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cell-primary landing-page-link"
                          title={page.url}
                        >
                          {formatPageUrl(page.url, 40)}
                        </a>
                      </td>
                      {hasSessions && (
                        <td style={{ textAlign: 'right' }}>
                          <span className="attr-num">{page.sessions == null ? '—' : formatNumber(page.sessions)}</span>
                        </td>
                      )}
                      <td style={{ textAlign: 'right' }}>
                        <span className="attr-num">{page.calls}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="cell-brand">{page.leads || 0}</span>
                      </td>
                      {hasSessions && (
                        <td style={{ textAlign: 'right' }}>
                          <ConversionCell row={page} />
                        </td>
                      )}
                      <td style={{ textAlign: 'right' }}>
                        <span className="attr-num">{formatCurrency(page.revenue || 0)}</span>
                      </td>
                    </tr>
                  ))}
                  {visibleRows.length === 0 && (
                    <tr>
                      <td colSpan={hasSessions ? 7 : 5} className="attr-empty-cell">
                        Geen pagina's gevonden{filterActive ? ` voor ${sourceFilter}` : ''} in deze periode.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            ) : (
              <table className="table-premium">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>PAGINA</th>
                    <th style={{ textAlign: 'right' }}>KLIKKERS</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((page, i) => (
                    <tr key={page.url}>
                      <td><span className="rank-badge">{i + 1}</span></td>
                      <td>
                        <span className="cell-primary" style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>
                          {formatPageUrl(page.url)}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <span className="cell-brand">{page.clickers}</span>
                      </td>
                    </tr>
                  ))}
                  {visibleRows.length === 0 && (
                    <tr>
                      <td colSpan={3} className="attr-empty-cell">
                        Geen tel-klikken gevonden{filterActive ? ` voor ${sourceFilter}` : ''} in deze periode.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
          {hasMoreRows && (
            <div style={{ padding: '8px 16px', borderTop: '1px solid var(--color-neutral-100)', textAlign: 'center' }}>
              <button
                type="button"
                className="funnel-show-more"
                onClick={() => setShowAllRows((prev) => !prev)}
              >
                {showAllRows ? 'Toon minder' : `Toon meer (${Math.min(activeRows.length, 25) - 5})`}
              </button>
            </div>
          )}
      </div>
    </div>
  );
};

export default AttributionSection;
